// Phase P.12 — Calendar Day view.
// Single-day agenda rendered as a vertical time-grid. Time column on the
// left, jobs on the right positioned by start-time and sized by duration.
//
// Design goals (per spec):
//  * Feel like Google Calendar / Notion Calendar — spacious, scannable.
//  * Hard-bounded 07:00–21:00, auto-widens toward 08:00–20:00 default.
//  * Payment-status colour tint on every card.
//  * Click an empty slot → prefill Quick Booking modal for that time.

import { useMemo } from "react";
import { ChevronLeft, ChevronRight, Plus, Ban, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  computeHourWindow, bookingBox, paymentTintClasses, fmtHm, fmtHmRange,
  assignOverlapLanes, laneRect, MAX_VISIBLE_LANES,
} from "@/lib/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const HOUR_PX = 56; // 56px/hour on desktop — enough for a customer name + subtitle.

function formatDayHeader(d) {
  return d.toLocaleDateString(undefined, {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });
}

// Phase P.27 — shared YMD helper used to key into `blocksByDate`/`notesByDate`.
function _ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function CalendarDayView({
  date,
  bookings,
  onBookingClick,
  onSlotClick,
  onPrev,
  onNext,
  onToday,
  isToday,
  // Phase P.27 — day-block + notes surfaces. All optional so legacy call
  // sites (tests, storybook) still render without them.
  blocksByDate,
  notesForDate,
  onBlockDay,
  onAddNote,
  onNoteClick,
}) {
  const dayBookings = useMemo(() => {
    if (!Array.isArray(bookings)) return [];
    return bookings
      .filter((b) => {
        if (!b?.scheduled_start) return false;
        const bd = new Date(b.scheduled_start);
        return bd.getFullYear() === date.getFullYear()
          && bd.getMonth() === date.getMonth()
          && bd.getDate() === date.getDate();
      })
      .sort((a, b) => (a.scheduled_start || "").localeCompare(b.scheduled_start || ""));
  }, [bookings, date]);

  const { lo, hi } = useMemo(() => computeHourWindow(dayBookings), [dayBookings]);
  const hours = useMemo(() => {
    const out = [];
    for (let h = lo; h <= hi; h++) out.push(h);
    return out;
  }, [lo, hi]);

  const totalHeight = (hi - lo) * HOUR_PX;

  const handleSlotClick = (h, e) => {
    // Ignore clicks that bubbled up from a booking card.
    if (e?.target && e.target.closest?.("[data-booking-tile]")) return;
    const d = new Date(date);
    d.setHours(h, 0, 0, 0);
    onSlotClick?.(d);
  };

  return (
    <div className="bg-card border border-border rounded-[10px] overflow-hidden" data-testid="calendar-day-view">
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-secondary/40">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={onPrev} data-testid="day-prev" aria-label="Previous day">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onToday} data-testid="day-today">Today</Button>
          <Button variant="outline" size="icon" onClick={onNext} data-testid="day-next" aria-label="Next day">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <h2 className={`text-lg font-black tracking-tight ${isToday ? "text-accent" : ""}`} data-testid="day-header">
          {formatDayHeader(date)}
        </h2>
        <div className="flex items-center gap-2">
          {/* Phase P.27 — quick actions: block/unblock day + add note. */}
          {onBlockDay && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onBlockDay(date)}
              className="h-8 gap-1"
              data-testid="day-block-btn"
              title={(blocksByDate && blocksByDate.get(_ymd(date))) ? "This day is blocked — edit or unblock" : "Block this day (holiday / day off)"}
            >
              <Ban className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{(blocksByDate && blocksByDate.get(_ymd(date))) ? "Blocked" : "Block day"}</span>
            </Button>
          )}
          {onAddNote && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const at = new Date(date); at.setHours(9, 0, 0, 0);
                onAddNote({ at });
              }}
              className="h-8 gap-1"
              data-testid="day-add-note-btn"
            >
              <StickyNote className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Add note</span>
            </Button>
          )}
          <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground min-w-[70px] text-right">
            {dayBookings.length} {dayBookings.length === 1 ? "job" : "jobs"}
          </div>
        </div>
      </header>

      {/* Phase P.27 — blocked-day banner. Rendered above the agenda so
          managers can see the block reason at a glance without hunting for
          the pill inside a booking column. */}
      {blocksByDate && blocksByDate.get(_ymd(date)) && (
        <div
          className="px-4 py-2 border-b border-destructive/20 bg-destructive/5 flex items-center gap-2"
          data-testid={`day-block-banner-${_ymd(date)}`}
        >
          <Ban className="h-3.5 w-3.5 text-destructive" />
          <span className="text-xs font-semibold text-destructive">
            Blocked: {blocksByDate.get(_ymd(date)).reason || "no reason given"}
          </span>
        </div>
      )}

      {/* Phase P.27.1 — empty state only when there are neither bookings NOR
          notes for the day. If notes exist, we still render the agenda
          gutter below so their time-anchored chips are visible. */}
      {dayBookings.length === 0 && (notesForDate || []).length === 0 && (
        <div className="text-center py-12 text-sm text-muted-foreground" data-testid="day-empty">
          No jobs scheduled for this day.
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => handleSlotClick(9)} className="gap-2">
              <Plus className="h-3.5 w-3.5" /> Book a job
            </Button>
          </div>
        </div>
      )}

      {(dayBookings.length > 0 || (notesForDate || []).length > 0) && (
        <div className="relative flex">
          {/* time gutter */}
          <div className="w-14 border-r border-border shrink-0" style={{ height: totalHeight }}>
            {hours.map((h) => (
              <div
                key={h}
                className="mono text-[10px] uppercase tracking-widest text-muted-foreground pr-2 text-right border-b border-border/60 flex items-start justify-end pt-0.5"
                style={{ height: HOUR_PX }}
              >
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>
          {/* agenda column */}
          <div
            className="relative flex-1 min-w-0"
            style={{ height: totalHeight }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const y = e.clientY - rect.top;
              const hour = lo + Math.floor(y / HOUR_PX);
              handleSlotClick(hour, e);
            }}
            data-testid="day-agenda-canvas"
          >
            {/* hourly grid lines */}
            {hours.map((h, idx) => (
              <div
                key={h}
                className="absolute left-0 right-0 border-b border-border/40 pointer-events-none"
                style={{ top: idx * HOUR_PX, height: HOUR_PX }}
              />
            ))}

            {/* booking cards — Phase P.14: overlap-aware lane layout so
                concurrent bookings for different staff render side-by-side
                instead of hiding each other. */}
            {(() => {
              const laned = assignOverlapLanes(dayBookings);
              // Build overflow groups (lane >= MAX_VISIBLE_LANES-1 in a
              // cluster with laneCount > MAX_VISIBLE_LANES). We render lanes
              // 0..MAX-2 normally, then a single "+N more" chip in the last
              // visual lane that opens a Popover with the hidden ones.
              const nodes = [];
              const overflowByStart = new Map(); // key = cluster anchor id → array
              laned.forEach((x) => {
                if (!x.laneCount || x.laneCount <= MAX_VISIBLE_LANES) return;
                if (x.lane < MAX_VISIBLE_LANES - 1) return;
                // Group by the cluster's laneCount + first item's start —
                // approximate by rounding start to minute; good enough for
                // rendering a single overflow chip per cluster.
                const key = `${x.laneCount}-${new Date(x.b.scheduled_start).toISOString().slice(0, 16)}`;
                const arr = overflowByStart.get(key) || [];
                arr.push(x);
                overflowByStart.set(key, arr);
              });
              laned.forEach((x) => {
                const { b, lane, laneCount } = x;
                const isOverflowItem = laneCount > MAX_VISIBLE_LANES && lane >= MAX_VISIBLE_LANES - 1;
                if (isOverflowItem) return; // handled below via popover chip
                const { top, height } = bookingBox(b, { lo, hourPx: HOUR_PX });
                const { leftPct, widthPct } = laneRect(lane, laneCount);
                const tint = paymentTintClasses(b);
                nodes.push(
                  <button
                    key={b.id}
                    type="button"
                    data-booking-tile
                    data-testid={`day-booking-${b.id}`}
                    onClick={(e) => { e.stopPropagation(); onBookingClick?.(b.id); }}
                    className={`absolute text-left rounded-[6px] border border-l-4 ${tint.border} ${tint.bg} px-3 py-2 hover:shadow-md transition-shadow overflow-hidden`}
                    style={{
                      top, height,
                      left: `calc(${leftPct}% + 8px)`,
                      width: `calc(${widthPct}% - 16px)`,
                    }}
                    title={`${b.customer_name || ""} · ${fmtHmRange(b)}${b.staff_name ? ` · ${b.staff_name}` : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-extrabold truncate">
                          {b.customer_name || "—"}
                        </div>
                        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                          {fmtHmRange(b)}
                        </div>
                      </div>
                      <span className={`inline-block h-2.5 w-2.5 rounded-full mt-0.5 ${tint.dot}`} title={tint.label} />
                    </div>
                    {b.staff_name && (
                      <div className="text-[10px] text-muted-foreground truncate mt-1">{b.staff_name}</div>
                    )}
                    {/* Phase P.19 — travel from previous job (same-staff same-day). */}
                    {b.travel_from_previous && (
                      <div className="mt-1">
                        <span
                          className="inline-flex items-center gap-0.5 mono text-[10px] uppercase tracking-wider text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-1.5 py-0.5"
                          title={`Travel from previous job · ${Math.round(Number(b.travel_from_previous.duration_min || 0))} min · ${b.travel_from_previous.distance_km} km`}
                          data-testid={`day-travel-pill-${b.id}`}
                        >
                          <span aria-hidden="true">↗</span>
                          {Math.round(Number(b.travel_from_previous.duration_min || 0))} min · {b.travel_from_previous.distance_km} km
                        </span>
                      </div>
                    )}
                    {Array.isArray(b.template_ids) && b.template_ids.length > 1 && (
                      <div
                        className="text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 inline-flex items-center gap-0.5 mt-1"
                        data-testid={`booking-multi-template-badge-${b.id}`}
                        title={(b.job_templates || []).map((t) => t.name).join(", ")}
                      >
                        +{b.template_ids.length - 1} services ⚠
                      </div>
                    )}
                    {b.booking_code && height > 60 && (
                      <div className="mono text-[9px] tracking-widest text-muted-foreground/80 mt-1">
                        {b.booking_code}
                      </div>
                    )}
                  </button>
                );
              });
              // Render overflow chips (one per cluster).
              overflowByStart.forEach((group, key) => {
                const anchor = group[0];
                const { top, height } = bookingBox(anchor.b, { lo, hourPx: HOUR_PX });
                const { leftPct, widthPct } = laneRect(anchor.lane, anchor.laneCount);
                nodes.push(
                  <Popover key={`overflow-${key}`}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-booking-tile
                        data-testid={`day-overflow-${key}`}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute text-left rounded-[6px] border border-dashed border-slate-400 bg-slate-100 px-2 py-1.5 hover:bg-slate-200 transition-colors overflow-hidden flex items-center justify-center"
                        style={{
                          top, height,
                          left: `calc(${leftPct}% + 8px)`,
                          width: `calc(${widthPct}% - 16px)`,
                        }}
                      >
                        <div className="text-center">
                          <div className="text-xs font-black text-slate-700">+{group.length} more</div>
                          <div className="mono text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">tap to view</div>
                        </div>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72" align="start">
                      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                        // {group.length} more at this slot
                      </div>
                      <div className="space-y-1.5 max-h-72 overflow-y-auto">
                        {group.map((x) => (
                          <button
                            key={x.b.id}
                            type="button"
                            onClick={() => onBookingClick?.(x.b.id)}
                            className="w-full text-left border border-border rounded p-2 hover:bg-secondary/60"
                            data-testid={`day-overflow-item-${x.b.id}`}
                          >
                            <div className="text-xs font-bold truncate">{x.b.customer_name || "—"}</div>
                            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                              {fmtHmRange(x.b)}{x.b.staff_name ? ` · ${x.b.staff_name}` : ""}
                            </div>
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              });
              // Phase P.27 — render small note chips at their minute-precision
              // position on the far right of the agenda so they never overlap
              // a booking card. Half the height of a booking (~28px) and a
              // muted amber tint so they're visibly informational, not blocking.
              (notesForDate || []).forEach((n) => {
                if (!n?.at) return;
                const t = new Date(n.at);
                if (Number.isNaN(t.getTime())) return;
                const minutesFromLo = (t.getHours() - lo) * 60 + t.getMinutes();
                if (minutesFromLo < 0 || minutesFromLo > (hi - lo) * 60) return;
                const top = (minutesFromLo / 60) * HOUR_PX;
                nodes.push(
                  <button
                    key={`note-${n.id}`}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onNoteClick?.(n); }}
                    data-testid={`day-note-${n.id}`}
                    data-booking-tile
                    className="absolute right-2 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 px-2 py-1 shadow-sm text-left overflow-hidden"
                    style={{ top, height: 28, maxWidth: "42%" }}
                    title={n.note}
                  >
                    <div className="flex items-center gap-1">
                      <StickyNote className="h-3 w-3 shrink-0 opacity-70" />
                      <span className="mono text-[10px] uppercase tracking-widest opacity-70">{fmtHm(t)}</span>
                    </div>
                    <div className="text-[11px] font-medium truncate leading-tight">{n.note}</div>
                  </button>
                );
              });
              return nodes;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
